import { useEffect, useMemo, useState } from 'react';
import { fetchRestaurantsByIds, fetchRestaurantsByPlaceIds } from '../../lib/api.js';

/**
 * The restaurants a group's deck needs, on top of the ones already loaded.
 *
 * A guest's own catalogue is built around where *they* are, so the host's deck
 * usually is not in it. Whatever is missing is fetched by id, which is what
 * lets everyone in a group vote on the same cards.
 *
 * Mirrors mobile/src/features/group/useGroupDeckRestaurants.ts.
 *
 * @param {import('./sync/index.js').GroupDeckSelection | null} selection
 * @param {import('../../lib/types.js').Restaurant[]} catalogue
 */
export function useGroupDeckRestaurants(selection, catalogue) {
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);

  const missing = useMemo(() => {
    if (selection?.mode !== 'manual') return { restaurantIds: [], placeIds: [], key: '' };
    const have = new Set(catalogue.map((restaurant) => restaurant.id));
    const restaurantIds = selection.restaurantIds.filter(
      (id) => !have.has(id) && !selection.googlePlaceIds?.[String(id)]
    );
    const placeIds = selection.restaurantIds
      .filter((id) => !have.has(id))
      .map((id) => selection.googlePlaceIds?.[String(id)])
      .filter(Boolean);
    return { restaurantIds, placeIds, key: `${restaurantIds.join(',')}|${placeIds.join(',')}` };
  }, [selection, catalogue]);

  useEffect(() => {
    if (!missing.key || missing.key === '|') {
      setFetched([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    Promise.all([
      fetchRestaurantsByIds(missing.restaurantIds),
      fetchRestaurantsByPlaceIds(missing.placeIds),
    ]).then(([stored, live]) => {
      if (!active) return;
      setFetched([...(stored ?? []), ...(live ?? [])]);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [missing.key, missing.placeIds, missing.restaurantIds]);

  const restaurants = useMemo(() => [...catalogue, ...fetched], [catalogue, fetched]);
  return { restaurants, loading };
}
